import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { orderAPI, customerAPI, productAPI } from '../../services/api';
import { todayKey } from '../../utils/date';
import '../Customers/Customers.css';

const OrderForm = ({ orderId, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const params = useParams();
  // If orderId prop provided, use it; otherwise check URL params
  const id = orderId || params.id;
  const isEdit = Boolean(id);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLocked, setIsLocked] = useState(false); // True when order is on confirmed/live/completed delivery run
  const [lockReason, setLockReason] = useState(''); // Reason for lock
  const [notice, setNotice] = useState(''); // Fields the server could not apply

  // Customer search state
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const customerSearchTimeout = useRef(null);
  const customerDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    order_external_reference: '',
    order_date: todayKey(),
    schedule_date: '',
    delivery_address: '',
    payment_received: false,
    payment_method: '',
    note: '',
    order_lines: [{ product_id: '', qty: 1, size: '', weight_code: '', unit_price: '' }],
  });

  useEffect(() => {
    fetchProducts();
    if (isEdit) {
      fetchOrder();
    }
    // Close dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (customerSearchTimeout.current) {
        clearTimeout(customerSearchTimeout.current);
      }
    };
  }, [id]);

  const handleClickOutside = (event) => {
    if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
      setCustomerDropdownOpen(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productAPI.getAll({ per_page: 100 });
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const searchCustomers = async (query) => {
    if (!query.trim()) {
      setCustomerSearchResults([]);
      return;
    }
    
    setSearchingCustomers(true);
    try {
      const response = await customerAPI.search(query);
      setCustomerSearchResults(response.data || []);
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const handleCustomerSearchChange = (e) => {
    const query = e.target.value;
    setCustomerSearchQuery(query);
    setCustomerDropdownOpen(true);
    
    // Debounce search
    if (customerSearchTimeout.current) {
      clearTimeout(customerSearchTimeout.current);
    }
    customerSearchTimeout.current = setTimeout(() => {
      searchCustomers(query);
    }, 300);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearchQuery(customer.fullname);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id.toString(),
      delivery_address: customer.delivery_address || prev.delivery_address,
    }));
    setCustomerDropdownOpen(false);
    setCustomerSearchResults([]);
  };

  const clearCustomerSelection = () => {
    setSelectedCustomer(null);
    setCustomerSearchQuery('');
    setFormData(prev => ({
      ...prev,
      customer_id: '',
    }));
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await orderAPI.getById(id);
      const order = response;
      
      // Set selected customer if customer_id exists
      let customer = null;
      if (order.customer_id) {
        try {
          customer = await customerAPI.getById(order.customer_id);
        } catch (e) {
          console.error('Error fetching customer for order:', e);
        }
      }
      
      if (customer) {
        setSelectedCustomer(customer);
        setCustomerSearchQuery(customer.fullname);
      }
      
      setFormData({
        customer_id: order.customer_id ? order.customer_id.toString() : '',
        order_external_reference: order.order_external_reference || '',
        order_date: order.order_date ? order.order_date.split('T')[0] : '',
        schedule_date: order.schedule_date ? order.schedule_date.split('T')[0] : '',
        delivery_address: order.delivery_address || '',
        payment_received: order.payment_received || false,
        payment_method: order.payment_method || '',
        note: order.note || '',
        order_lines: order.order_lines && order.order_lines.length > 0
          ? order.order_lines.map(line => ({
              id: line.id,
              product_id: line.product_id,
              qty: line.qty,
              size: line.size !== undefined && line.size !== null ? line.size : '',
              weight_code: line.weight_code || '',
              unit_price: line.unit_price || '',
            }))
          : [{ product_id: '', qty: 1, size: '', weight_code: '', unit_price: '' }],
      });

      // Check if order is on a confirmed or later delivery run
      // Status: 1=Planning, 2=Confirmed, 3=Live, 4=Completed
      if (order.deliveries && order.deliveries.length > 0) {
        const hasConfirmedRun = order.deliveries.some(d => {
          const runStatus = d.delivery_run?.status;
          return runStatus && runStatus >= 2;
        });
        if (hasConfirmedRun) {
          setIsLocked(true);
          setLockReason('This order is on a confirmed delivery run. The products and the schedule date cannot be changed, but the payment method, payment status, delivery address and notes can still be edited.');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1: return 'Planning';
      case 2: return 'Confirmed';
      case 3: return 'Live';
      case 4: return 'Completed';
      default: return 'Unknown';
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleOrderLineChange = (index, field, value) => {
    setFormData(prev => {
      const lines = [...prev.order_lines];
      lines[index] = { ...lines[index], [field]: value };
      
      // When product is selected, auto-populate size and weight_code from product
      if (field === 'product_id' && value) {
        const selectedProduct = products.find(p => p.id.toString() === value);
        if (selectedProduct) {
          lines[index].size = selectedProduct.size || '';
          lines[index].weight_code = selectedProduct.weight_code || '';
        }
      }
      
      return { ...prev, order_lines: lines };
    });
  };

  const addOrderLine = () => {
    setFormData(prev => ({
      ...prev,
      order_lines: [...prev.order_lines, { product_id: '', qty: 1, size: '', weight_code: '', unit_price: '' }],
    }));
  };

  const removeOrderLine = (index) => {
    if (formData.order_lines.length === 1) return;
    setFormData(prev => ({
      ...prev,
      order_lines: prev.order_lines.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate customer is selected
    if (!formData.customer_id) {
      setError('Please select a customer');
      return;
    }

    // Validate at least one order line with product
    const validLines = formData.order_lines.filter(line => line.product_id && line.qty > 0);
    if (validLines.length === 0) {
      setError('At least one product must be added to the order');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setNotice('');

      // A confirmed run freezes the products and the run's date, but the rest
      // of the order - payment, address, notes - stays editable. Strip the
      // line items from the payload rather than sending them and having the
      // API reject them.
      const payload = isLocked ? { ...formData, order_lines: undefined } : formData;

      const result = isEdit
        ? await orderAPI.update(id, payload)
        : await orderAPI.create(payload);

      // The server refuses anything it could not apply. Say what was
      // skipped instead of reporting a plain success.
      const skipped = Object.values(result?.not_applied || {});
      if (skipped.length > 0) {
        setNotice(skipped.join(' '));
        // The order on the server may now differ from what is on screen, so
        // reload it rather than sending the operator somewhere else.
        if (isEdit) {
          await fetchOrder();
        }
        return;
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/orders');
      }
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} order`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/orders');
    }
  };

  // If used as modal, wrap in modal structure
  if (onClose && onSuccess) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{isEdit ? 'Edit Order' : 'New Order'}</h2>
            <button className="modal-close" onClick={handleClose}>&times;</button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="loading">Loading order...</div>
            ) : (
              <form onSubmit={handleSubmit} className="customer-form">
                {error && <div className="error-message">{error}</div>}
                {notice && <div className="notice-message">{notice}</div>}

                <div className="form-section">
                  <h3>Customer & Order Details</h3>

                  <div className="form-grid-2">
                    <div className="form-group">
                      <label htmlFor="customer_search">Customer *</label>
                      <div className="customer-search-container" ref={customerDropdownRef}>
                        <div className="customer-search-input-wrapper">
                          <input
                            type="text"
                            id="customer_search"
                            value={customerSearchQuery}
                            onChange={handleCustomerSearchChange}
                            onFocus={() => customerSearchQuery.trim() && setCustomerDropdownOpen(true)}
                            placeholder="Search by name, phone, email or address..."
                            autoComplete="off"
                            required
                          />
                          {selectedCustomer && (
                            <button
                              type="button"
                              className="customer-search-clear"
                              onClick={clearCustomerSelection}
                              title="Clear selection"
                            >
                              &times;
                            </button>
                          )}
                        </div>
                        {customerDropdownOpen && (
                          <div className="customer-search-dropdown">
                            {searchingCustomers ? (
                              <div className="customer-search-loading">Searching...</div>
                            ) : customerSearchResults.length > 0 ? (
                              customerSearchResults.map(customer => (
                                <div
                                  key={customer.id}
                                  className="customer-search-result"
                                  onClick={() => handleCustomerSelect(customer)}
                                >
                                  <div className="customer-search-result-name">{customer.fullname}</div>
                                  <div className="customer-search-result-details">
                                    {customer.phone && <span>{customer.phone}</span>}
                                    {customer.email && <span>{customer.email}</span>}
                                    {customer.delivery_address && <span>{customer.delivery_address}</span>}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="customer-search-no-results">No customers found</div>
                            )}
                          </div>
                        )}
                        <input type="hidden" name="customer_id" value={formData.customer_id} />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="order_external_reference">External Reference</label>
                      <input
                        type="text"
                        id="order_external_reference"
                        name="order_external_reference"
                        value={formData.order_external_reference}
                        onChange={handleChange}
                        placeholder="Optional reference number"
                      />
                    </div>
                    <div className="form-group"></div>

                    <div className="form-group">
                      <label htmlFor="order_date">Order Date *</label>
                      <input
                        type="date"
                        id="order_date"
                        name="order_date"
                        value={formData.order_date}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="schedule_date">Schedule Date</label>
                      <input
                        type="date"
                        id="schedule_date"
                        name="schedule_date"
                        value={formData.schedule_date}
                        onChange={handleChange}
                        disabled={isLocked}
                        title={isLocked ? 'The schedule date cannot be changed while the order is on a confirmed delivery run.' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label>Payment Method</label>
                      <div className="radio-group-horizontal">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="payment_method"
                            value="cash"
                            checked={formData.payment_method === 'cash'}
                            onChange={handleChange}
                          />
                          Cash
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="payment_method"
                            value="card"
                            checked={formData.payment_method === 'card'}
                            onChange={handleChange}
                          />
                          Card
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="payment_method"
                            value="bank_transfer"
                            checked={formData.payment_method === 'bank_transfer'}
                            onChange={handleChange}
                          />
                          Bank Transfer
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="payment_method"
                            value="account"
                            checked={formData.payment_method === 'account'}
                            onChange={handleChange}
                          />
                          Account
                        </label>
                      </div>
                      <br />
                    </div>

                    <div className="form-group ">
                      <label>Payment Received </label>

                      <div className="radio-group-horizontal">
                        <label className="radio-label">
                          <input
                            type="checkbox"
                            name="payment_received"
                            checked={formData.payment_received}
                            onChange={handleChange}
                          /> 
                          
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="delivery_address">Delivery Address</label>
                    <textarea
                      id="delivery_address"
                      name="delivery_address"
                      value={formData.delivery_address}
                      onChange={handleChange}
                      rows="2"
                      placeholder="Delivery address (defaults to customer's address)"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="note">Notes</label>
                    <textarea
                      id="note"
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      rows="2"
                      placeholder="Optional notes"
                    />
                  </div>

                </div>

                <div className="form-section">
                  <h3>Order Items</h3>

                  {isLocked && (
                    <div className="warning-message" style={{ padding: '12px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', marginBottom: '16px' }}>
                      <strong>Locked:</strong> {lockReason}
                    </div>
                  )}

                  {formData.order_lines.map((line, index) => (
                    
                    <div key={index} className="order-line-row">

                      <div className="form-grid-4 row">
                        
                        <div className="form-group">
                          <label>Product *</label>
                          <select
                            value={line.product_id}
                            onChange={(e) => handleOrderLineChange(index, 'product_id', e.target.value)}
                            required
                            disabled={isLocked}
                          >
                            <option value="">Select product</option>
                            {products.map(product => (
                              <option key={product.id} value={product.id}>
                                {product.code} - {product.description}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Qty *</label>
                          <input
                            type="number"
                            value={line.qty}
                            onChange={(e) => handleOrderLineChange(index, 'qty', parseInt(e.target.value) || 0)}
                            min="1"
                            required
                            disabled={isLocked}
                          />
                        </div>

                        <div className="form-group">
                          <label>Size</label>
                          <select
                            value={line.size}
                            onChange={(e) => handleOrderLineChange(index, 'size', e.target.value)}
                            disabled={isLocked}
                          >
                            <option value="">Select size</option>
                            <option value="0">0 - Note Only</option>
                            <option value="1">1 Cube</option>
                            <option value="7">7 Cube</option>
                            <option value="13">13 Cube</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Weight</label>
                          <select
                            value={line.weight_code}
                            onChange={(e) => handleOrderLineChange(index, 'weight_code', e.target.value)}
                            disabled={isLocked}
                          >
                            <option value="">Select weight</option>
                            <option value="1">Small</option>
                            <option value="2">Medium</option>
                            <option value="3">Heavy</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Unit Price</label>
                          <input
                            type="number"
                            value={line.unit_price || ''}
                            onChange={(e) => handleOrderLineChange(index, 'unit_price', parseFloat(e.target.value) || null)}
                            step="0.01"
                            min="0"
                            disabled={isLocked}
                          />
                        </div>

                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeOrderLine(index)}
                          disabled={formData.order_lines.length === 1 || isLocked}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={addOrderLine}
                    disabled={isLocked}
                  >
                    + Add Product
                  </button>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Saving...' : (isEdit ? 'Update Order' : 'Create Order')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Non-modal version for direct route access
  if (loading) {
    return <div className="loading">Loading order...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>{isEdit ? 'Edit Order' : 'New Order'}</h1>
            </div>

            {error && <div className="error-message">{error}</div>}
            {notice && <div className="notice-message">{notice}</div>}

            <form onSubmit={handleSubmit} className="customer-form">
              <div className="form-section">
                <h3>Customer & Order Details</h3>

                  <div className="form-group">
                    <label htmlFor="customer_search">Customer *</label>
                    <div className="customer-search-container" ref={customerDropdownRef}>
                      <div className="customer-search-input-wrapper">
                        <input
                          type="text"
                          id="customer_search"
                          value={customerSearchQuery}
                          onChange={handleCustomerSearchChange}
                          onFocus={() => customerSearchQuery.trim() && setCustomerDropdownOpen(true)}
                          placeholder="Search by name, phone, email or address..."
                          autoComplete="off"
                          required
                        />
                        {selectedCustomer && (
                          <button
                            type="button"
                            className="customer-search-clear"
                            onClick={clearCustomerSelection}
                            title="Clear selection"
                          >
                            &times;
                          </button>
                        )}
                      </div>
                      {customerDropdownOpen && (
                        <div className="customer-search-dropdown">
                          {searchingCustomers ? (
                            <div className="customer-search-loading">Searching...</div>
                          ) : customerSearchResults.length > 0 ? (
                            customerSearchResults.map(customer => (
                              <div
                                key={customer.id}
                                className="customer-search-result"
                                onClick={() => handleCustomerSelect(customer)}
                              >
                                <div className="customer-search-result-name">{customer.fullname}</div>
                                <div className="customer-search-result-details">
                                  {customer.phone && <span>{customer.phone}</span>}
                                  {customer.email && <span>{customer.email}</span>}
                                  {customer.delivery_address && <span>{customer.delivery_address}</span>}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="customer-search-no-results">No customers found</div>
                          )}
                        </div>
                      )}
                      <input type="hidden" name="customer_id" value={formData.customer_id} />
                    </div>
                  </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label htmlFor="order_external_reference">External Reference</label>
                    <input
                      type="text"
                      id="order_external_reference"
                      name="order_external_reference"
                      value={formData.order_external_reference}
                      onChange={handleChange}
                      placeholder="Optional reference number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="order_date">Order Date *</label>
                    <input
                      type="date"
                      id="order_date"
                      name="order_date"
                      value={formData.order_date}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="schedule_date">Schedule Date</label>
                    <input
                      type="date"
                      id="schedule_date"
                      name="schedule_date"
                      value={formData.schedule_date}
                      onChange={handleChange}
                      disabled={isLocked}
                      title={isLocked ? 'The schedule date cannot be changed while the order is on a confirmed delivery run.' : ''}
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Method</label>
                    <div className="radio-group-horizontal">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="payment_method"
                          value="cash"
                          checked={formData.payment_method === 'cash'}
                          onChange={handleChange}
                        />
                        Cash
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="payment_method"
                          value="card"
                          checked={formData.payment_method === 'card'}
                          onChange={handleChange}
                        />
                        Card
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="payment_method"
                          value="bank_transfer"
                          checked={formData.payment_method === 'bank_transfer'}
                          onChange={handleChange}
                        />
                        Bank Transfer
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="payment_method"
                          value="account"
                          checked={formData.payment_method === 'account'}
                          onChange={handleChange}
                        />
                        Account
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                      <label>Payment Received </label>
                      <div className="radio-group-horizontal">
                        <label className="radio-label">
                          <input
                            type="checkbox"
                            name="payment_received"
                            checked={formData.payment_received}
                            onChange={handleChange}
                          /> 
                          
                        </label>
                      </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="delivery_address">Delivery Address</label>
                  <textarea
                    id="delivery_address"
                    name="delivery_address"
                    value={formData.delivery_address}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Delivery address (defaults to customer's address)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="note">Notes</label>
                  <textarea
                    id="note"
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Optional notes"
                  />
                </div>

              </div>

              <div className="form-section">
                <h3>Order Items</h3>

                {isLocked && (
                  <div className="warning-message" style={{ padding: '12px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', marginBottom: '16px' }}>
                    <strong>Locked:</strong> {lockReason}
                  </div>
                )}

                {formData.order_lines.map((line, index) => (
                  <div key={index} className="order-line-row">

                    <div className="form-grid-4 row">
                      
                      <div className="form-group">
                        <label>Product *</label>
                        <select
                          value={line.product_id}
                          onChange={(e) => handleOrderLineChange(index, 'product_id', e.target.value)}
                          required
                          disabled={isLocked}
                        >
                          <option value="">Select product</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.code} - {product.description}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Qty *</label>
                        <input
                          type="number"
                          value={line.qty}
                          onChange={(e) => handleOrderLineChange(index, 'qty', parseInt(e.target.value) || 0)}
                          min="1"
                          required
                          disabled={isLocked}
                        />
                      </div>

                      <div className="form-group">
                        <label>Size</label>
                        <select
                          value={line.size}
                          onChange={(e) => handleOrderLineChange(index, 'size', e.target.value)}
                          disabled={isLocked}
                        >
                          <option value="">Select size</option>
                          <option value="0">0 - Note Only</option>
                          <option value="1">1 Cube</option>
                          <option value="7">7 Cube</option>
                          <option value="13">13 Cube</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Weight</label>
                        <select
                          value={line.weight_code}
                          onChange={(e) => handleOrderLineChange(index, 'weight_code', e.target.value)}
                          disabled={isLocked}
                        >
                          <option value="">Select weight</option>
                          <option value="1">Small</option>
                          <option value="2">Medium</option>
                          <option value="3">Heavy</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Unit Price</label>
                        <input
                          type="number"
                          value={line.unit_price || ''}
                          onChange={(e) => handleOrderLineChange(index, 'unit_price', parseFloat(e.target.value) || null)}
                          step="0.01"
                          min="0"
                          disabled={isLocked}
                        />
                      </div>

                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeOrderLine(index)}
                        disabled={formData.order_lines.length === 1 || isLocked}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addOrderLine}
                  disabled={isLocked}
                >
                  + Add Product
                </button>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : (isEdit ? 'Update Order' : 'Create Order')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/orders')}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default OrderForm;